import type {ChatMessage, UnwrappedReplyMessage} from '../../models'

export interface LayoutRectangle {
  x: number
  y: number
  width: number
  height: number
}

export type WsEvents =
  | 'error'
  | 'newMessage'
  | 'onUploadProgress'
  | 'onSendMessageError'
  | 'updateChatsList'
  | 'newMessagesCount'
  | 'blockUser'
  | 'onDeleteChat'
  | 'onBlockChat'
  | 'onMessageRead'
  | 'onMessageReceived'

export type chatEvents =
  | 'onRequestUpdateAllLayouts'
  | 'onRequestUpdateItemLayout'
  | 'onRepliedClick'
  | 'onOpenMessageDropDown'
  | 'onMessageClick'
  | 'onMessageLongClick'
  | 'onMessageOutsideClick'
  | 'onMessageOutsideLongClick'
  | 'onProductClick'
  | 'onSocketConnectionChanged'

export type ChatEvent = WsEvents | chatEvents

type Listener = (...args: any) => void

export interface DropDownParams {
  readonly viewFrame: LayoutRectangle | null
}

export const chatEventEmitter = {
  listeners: new Map<ChatEvent, Array<Listener>>(),

  trigger(event: ChatEvent, ...args: any) {
    this.listeners.get(event)?.forEach((l) => l(...args))
  },

  once(event: ChatEvent, callback: Listener): () => void {
    this.listeners.delete(event)
    this.listeners.has(event) || this.listeners.set(event, [])
    this.listeners.get(event)?.push(callback)
    return () => this.off(event, callback)
  },

  on(event: ChatEvent, callback: Listener): () => void {
    this.listeners.has(event) || this.listeners.set(event, [])
    if (!this.listeners.get(event)?.find((c) => c === callback))
      this.listeners.get(event)?.push(callback)
    return () => this.off(event, callback)
  },

  off(event: ChatEvent, callback: Listener) {
    const listeners = this.listeners.get(event)
    if (!listeners) return
    if (listeners.length === 0) return
    this.listeners.set(
      event,
      listeners.filter((l) => l !== callback),
    )
  },

  fireRequestUpdateItemLayout(
    messageId: string,
    overrides: Partial<ChatMessage>,
    resolve: () => void,
  ) {
    this.trigger('onRequestUpdateItemLayout', messageId, overrides, resolve)
  },

  fireRequestUpdateAllLayouts(
    overrides: Partial<ChatMessage>,
    resolve: () => void,
  ) {
    this.trigger('onRequestUpdateAllLayouts', overrides, resolve)
  },

  fireRepliedClick(replied: UnwrappedReplyMessage) {
    this.trigger('onRepliedClick', replied)
  },

  fireOpenMessageDropDown(message: ChatMessage, params: DropDownParams) {
    this.trigger('onOpenMessageDropDown', message, params)
  },

  fireMessageClick(message: ChatMessage, params?: DropDownParams) {
    this.trigger('onMessageClick', message, params)
  },

  fireMessageLongClick(message: ChatMessage) {
    this.trigger('onMessageLongClick', message)
  },

  fireMessageOutsideClick(message: ChatMessage, params?: DropDownParams) {
    this.trigger('onMessageOutsideClick', message, params)
  },

  fireMessageOutsideLongClick(message: ChatMessage) {
    this.trigger('onMessageOutsideLongClick', message)
  },
}
